import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Drawer, Typography } from '@mui/material';
import { ArrowIcon, FileAddIcon } from '@sharedCR/coreDesignSystem/icons';
import { deleteCapturedTempFile, scanDocuments } from 'app/services/nativeCamera';
import TextFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/TextFieldCDS';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { getIsNativePlatform } from 'utils/platform';

import { showErrorMessageAction, showMessageAction } from 'store/global/actions';

import CaptureThumbnail from './components/CaptureThumbnail';
import {
  ACCEPTED_IMAGE_TYPES,
  ADD_MORE_LABEL,
  ADD_SOURCE_LABEL,
  EMPTY_STATE_COPY,
} from './DocumentCaptureSession.constants';
import {
  addSourceStyles,
  bodyStyles,
  closeButtonStyles,
  continueButtonStyles,
  drawerPaperStyles,
  emptyActionStyles,
  emptyStateStyles,
  footerStyles,
  headerStyles,
  hiddenInputStyles,
  labelFieldStyles,
  thumbnailGridStyles,
} from './DocumentCaptureSession.styles';
import { DocumentCaptureSessionTestIds } from './DocumentCaptureSession.testIds';
import {
  createCaptureDocument,
  createCaptureDocumentsFromFiles,
  revokePreviewUrl,
  uploadCaptureBatch,
} from './DocumentCaptureSession.utils';

const drawerPaperProps = { sx: drawerPaperStyles };

/**
 * Multi-document capture session reached from the "+" → object → "Start with
 * photos" fork (DEV-4252 / DEV-4253).
 *
 * The user captures or selects MULTIPLE document pages, sees a thumbnail batch,
 * names each page + the batch, removes/retakes, then proceeds.
 *  - WEB: a multi-select `image/*` file input (browser-verifiable path).
 *  - NATIVE: the `@capacitor/camera` multi-shot loop (one page per tap).
 *
 * UPLOAD SEAM: uploading reuses the DEV-4250 `documentsApi.uploadDocument` →
 * server-side-extraction path, which requires a target `applicationId`. That id
 * is produced by the DOWNSTREAM draft-create flow (DEV-4251, not built here):
 *  - when `applicationId` is provided, the batch is uploaded directly;
 *  - otherwise the batch is handed to `onSubmit` so the caller can run
 *    draft-create-then-upload.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} [props.objectLabel] - "Loan" / "Contact" / "Lead" for the header
 * @param {string|null} [props.applicationId] - upload target (downstream seam)
 * @param {Function} [props.onSubmit] - (documents) => void; the draft-create handoff
 */
const DocumentCaptureSession = ({
  isOpen,
  onClose,
  objectLabel = '',
  applicationId = null,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const documentsRef = useRef([]);

  const [documents, setDocuments] = useState([]);
  const [batchLabel, setBatchLabel] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isNative = getIsNativePlatform();
  const hasDocuments = documents.length > 0;

  const releaseAll = useCallback(() => {
    setDocuments(prev => {
      prev.forEach(document => revokePreviewUrl(document.previewUrl));

      return [];
    });
    setBatchLabel('');
  }, []);

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  useEffect(
    () => () => {
      documentsRef.current.forEach(document => revokePreviewUrl(document.previewUrl));
    },
    [],
  );

  const resetAndClose = useCallback(() => {
    releaseAll();
    onClose();
  }, [releaseAll, onClose]);

  const onWebInputChange = useCallback(event => {
    const captured = createCaptureDocumentsFromFiles(event.target.files);

    setDocuments(prev => [...prev, ...captured]);
    event.target.value = '';
  }, []);

  const onOpenWebPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const onScanNative = useCallback(async () => {
    const scanned = await scanDocuments();

    if (!scanned.length) {
      return;
    }

    setDocuments(prev => [
      ...prev,
      ...scanned.map(page => createCaptureDocument(page.file, { nativePath: page.nativePath })),
    ]);
  }, []);

  const onAddSource = isNative ? onScanNative : onOpenWebPicker;

  const onRename = useCallback((id, name) => {
    setDocuments(prev =>
      prev.map(document => (document.id === id ? { ...document, name } : document)),
    );
  }, []);

  const onRemove = useCallback(id => {
    setDocuments(prev => {
      const removed = prev.find(document => document.id === id);

      if (removed) {
        revokePreviewUrl(removed.previewUrl);

        if (removed.nativePath) {
          deleteCapturedTempFile(removed.nativePath);
        }
      }

      return prev.filter(document => document.id !== id);
    });
  }, []);

  const onRetake = useCallback(
    async id => {
      onRemove(id);
      await onScanNative();
    },
    [onRemove, onScanNative],
  );

  const onBatchLabelChange = useCallback(event => setBatchLabel(event.target.value), []);

  const onContinue = useCallback(async () => {
    if (!hasDocuments) {
      return;
    }

    if (!applicationId) {
      onSubmit?.(documents);
      resetAndClose();

      return;
    }

    setIsSubmitting(true);

    try {
      const { fulfilledIds, failedCount } = await uploadCaptureBatch(documents, applicationId);

      const uploaded = documents.filter(document => fulfilledIds.includes(document.id));

      uploaded.forEach(document => {
        if (document.nativePath) {
          deleteCapturedTempFile(document.nativePath);
        }
      });

      if (fulfilledIds.length > 0) {
        dispatch(
          showMessageAction({
            message: `${fulfilledIds.length} document${
              fulfilledIds.length === 1 ? '' : 's'
            } uploaded.${failedCount > 0 ? ` ${failedCount} failed.` : ''}`,
            variant: 'success',
          }),
        );
      }

      if (failedCount > 0 && fulfilledIds.length === 0) {
        dispatch(showErrorMessageAction({ message: 'Upload failed. Please try again.' }));

        return;
      }

      onSubmit?.(uploaded);
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [hasDocuments, applicationId, documents, onSubmit, resetAndClose, dispatch]);

  const platformKey = isNative ? 'native' : 'web';
  const continueLabel = hasDocuments ? `Continue with ${documents.length}` : 'Continue';

  return (
    <Drawer
      PaperProps={drawerPaperProps}
      anchor="bottom"
      data-testid={DocumentCaptureSessionTestIds.DRAWER}
      open={isOpen}
      onClose={resetAndClose}
    >
      <Box sx={headerStyles}>
        <Box
          aria-label="Close"
          component="button"
          data-testid={DocumentCaptureSessionTestIds.CLOSE}
          sx={closeButtonStyles}
          type="button"
          onClick={resetAndClose}
        >
          <ArrowIcon direction={IconDirection.LEFT} size={IconSize[20]} />
        </Box>
        <Typography variant="medium20">
          {objectLabel ? `Photos for ${objectLabel}` : 'Add photos'}
        </Typography>
      </Box>

      <Box sx={bodyStyles}>
        {hasDocuments ? (
          <>
            <Box sx={thumbnailGridStyles}>
              {documents.map((document, index) => (
                <CaptureThumbnail
                  document={document}
                  index={index}
                  key={document.id}
                  onRemove={onRemove}
                  onRename={onRename}
                  onRetake={isNative ? onRetake : undefined}
                />
              ))}
            </Box>
            <Button
              data-testid={DocumentCaptureSessionTestIds.ADD_MORE}
              startIcon={<FileAddIcon size={IconSize[20]} />}
              sx={addSourceStyles}
              variant="text"
              onClick={onAddSource}
            >
              <Typography variant="medium14">{ADD_MORE_LABEL[platformKey]}</Typography>
            </Button>
            <Box sx={labelFieldStyles}>
              <TextFieldCDS
                dataTestId={DocumentCaptureSessionTestIds.BATCH_LABEL}
                label="Batch name (optional)"
                name="capture-session-batch-label"
                value={batchLabel}
                onChange={onBatchLabelChange}
              />
            </Box>
          </>
        ) : (
          <Box sx={emptyStateStyles}>
            <FileAddIcon size={IconSize[48]} />
            <Typography variant="medium16">{EMPTY_STATE_COPY.title}</Typography>
            <Typography color="text.secondary" variant="regular14">
              {EMPTY_STATE_COPY.subtitle}
            </Typography>
            <Button
              data-testid={DocumentCaptureSessionTestIds.ADD_SOURCE}
              sx={emptyActionStyles}
              variant="contained"
              onClick={onAddSource}
            >
              <Typography variant="medium14">{ADD_SOURCE_LABEL[platformKey]}</Typography>
            </Button>
          </Box>
        )}
      </Box>

      <input
        multiple
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        data-testid={DocumentCaptureSessionTestIds.FILE_INPUT}
        ref={inputRef}
        style={hiddenInputStyles}
        type="file"
        onChange={onWebInputChange}
      />

      <Box sx={footerStyles}>
        <Button
          data-testid={DocumentCaptureSessionTestIds.CONTINUE}
          disabled={!hasDocuments || isSubmitting}
          sx={continueButtonStyles}
          variant="contained"
          onClick={onContinue}
        >
          <Typography variant="medium16">{continueLabel}</Typography>
        </Button>
      </Box>
    </Drawer>
  );
};

DocumentCaptureSession.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  objectLabel: PropTypes.string,
  applicationId: PropTypes.string,
  onSubmit: PropTypes.func,
};

export default memo(DocumentCaptureSession);
